import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { API_URL } from "./services/api";
import { X } from "lucide-react";

import { useTranslation } from "react-i18next";

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdminCode, setShowAdminCode] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    adminCode: "",
  });

  // SECURITY: Admin code verification should be on the backend.
  // This client-side check is for UX only — the server must validate admin status.
  const ADMIN_CODE = process.env.REACT_APP_ADMIN_CODE;

  // Redirect when user is logged in
  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isLogin) {
        if (!formData.email || !formData.password) {
          throw new Error(t('login.errorFill'));
        }

        let response;
        try {
          response = await fetch(`${API_URL}/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
            }),
          });
        } catch (networkError) {
          console.error("Login Network Error:", networkError);
          throw new Error(t('login.errorNetwork') || "Network error. Please check your connection.");
        }

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Login failed");
        }

        login(data.user, data.token);

      } else {
        if (
          !formData.email ||
          !formData.password ||
          !formData.confirmPassword ||
          !formData.name
        ) {
          throw new Error(t('login.errorFill'));
        }
        if (formData.password !== formData.confirmPassword) {
          throw new Error(t('login.errorMatch'));
        }
        if (formData.password.length < 6) {
          throw new Error(t('login.errorLength'));
        }

        let isAdmin = false;
        if (formData.adminCode) {
          if (formData.adminCode !== ADMIN_CODE) {
            throw new Error(t('login.errorAdmin'));
          }
          isAdmin = true;
        }

        let response;
        try {
          response = await fetch(`${API_URL}/auth/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
              name: formData.name,
              isAdmin,
              adminCode: formData.adminCode,
            }),
          });
        } catch (networkError) {
          console.error("Registration Network Error:", networkError);
          throw new Error(t('login.errorNetwork') || "Network error. Please check your connection.");
        }

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Registration failed");
        }

        login(data.user, data.token);

      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-raya-card rounded-2xl shadow-2xl p-8 relative border border-raya-border">
        <button
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 text-raya-text-tertiary hover:text-raya-text-primary transition-colors"
          title="Cancel"
        >
          <X size={24} />
        </button>

        <h1 className="text-3xl font-bold text-center mb-8">
          {isLogin ? t('login.loginTitle') : t('login.signupTitle')}
        </h1>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg mb-6" role="alert" aria-live="polite">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-sm font-semibold mb-2">
                {t('login.fullName')}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('login.placeholderName')}
                className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold mb-2">{t('login.email')}</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('login.placeholderEmail')}
              className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-card focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">{t('login.password')}</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t('login.placeholderPassword')}
              className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-card focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
            />
          </div>

          {!isLogin && (
            <div>
              <label className="block text-sm font-semibold mb-2">
                {t('login.confirmPassword')}
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder={t('login.placeholderConfirm')}
                className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
              />
            </div>
          )}

          {!isLogin && (
            <div>
              <button
                type="button"
                onClick={() => setShowAdminCode(!showAdminCode)}
                className="text-sm text-raya-accent hover:underline w-full text-left mb-2"
              >
                {showAdminCode ? `▼ ${t('login.adminAccount')}` : `▶ ${t('login.adminAccount')}`}
              </button>
              {showAdminCode && (
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    {t('login.adminCode')}
                  </label>
                  <input
                    type="password"
                    name="adminCode"
                    value={formData.adminCode}
                    onChange={handleInputChange}
                    placeholder={t('login.placeholderAdminCode')}
                    className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                  />
                  <p className="text-xs text-raya-text-tertiary mt-1">
                    {t('login.adminHint')}
                  </p>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 disabled:opacity-50 text-black py-3 rounded-lg font-semibold transition transform hover:scale-[1.02] shadow-lg"
          >
            {loading ? t('login.processing') : isLogin ? t('login.submitLogin') : t('login.submitSignup')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-raya-text-secondary mb-2">
            {isLogin ? t('login.noAccount') : t('login.hasAccount')}
          </p>
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setFormData({
                email: "",
                password: "",
                confirmPassword: "",
                name: "",
              });
              setError(null);
            }}
            className="text-raya-accent hover:underline font-semibold"
          >
            {isLogin ? t('login.signupTitle') : t('login.loginTitle')}
          </button>
        </div>
      </div>
    </div>
  );
}
